// 🔧 assigned_volunteers_list.js
// ✅ Purpose: Displays assigned volunteers for a time slot
// ✅ Features:
// - Lists full name + email
// - Shows Delete button per volunteer with confirmation
// - Locally removes the volunteer from the list on success
// - Safely handles missing slot or assignedUsers data
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { logger } from "../utils/logger";

const AssignedVolunteersList = ({
  slot = {},
  activityId,
  eventId,
  setStatus,
}) => {
  // ✅ Safely initialize assigned volunteers
  const [localAssignedUsers, setLocalAssignedUsers] = useState(
    Array.isArray(slot?.assignedUsers) ? slot.assignedUsers : []
  );

  // 🧼 Keep local list in sync when parent slot updates
  useEffect(() => {
    setLocalAssignedUsers(
      Array.isArray(slot?.assignedUsers) ? slot.assignedUsers : []
    );
  }, [slot]);

  const handleRemove = async (uid) => {
    const user = localAssignedUsers.find((u) => u.uid === uid);

    const confirmed = window.confirm(
      `Are you sure you want to remove ${
        user?.firstName || "this volunteer"
      }?`
    );

    if (!confirmed) return;

    try {
      const updatedUsers = localAssignedUsers.filter((u) => u.uid !== uid);

      const slotRef = doc(
        db,
        "events",
        eventId,
        "volunteers",
        activityId,
        "timeSlots",
        slot.id
      );

      await updateDoc(slotRef, {
        assignedUsers: updatedUsers,
        assignedUserIds: updatedUsers.map((u) => u.uid),
      });

      setLocalAssignedUsers(updatedUsers);

      if (setStatus) {
        setStatus({
          type: "success",
          message: "✅ Volunteer removed.",
        });
      }
    } catch (err) {
      logger.error("❌ Failed to remove volunteer:", {
        value: err,
      });

      if (setStatus) {
        setStatus({
          type: "error",
          message: "❌ Failed to remove volunteer.",
        });
      }
    }
  };

  const updateVolunteerStatus = async (uid, field, value) => {
    const updatedUsers = localAssignedUsers.map((user) =>
      user.uid === uid ? { ...user, [field]: value } : user
    );
    try {
      await updateDoc(doc(db, "events", eventId, "volunteers", activityId, "timeSlots", slot.id), {
        assignedUsers: updatedUsers,
        assignedUserIds: updatedUsers.map((user) => user.uid),
      });
      setLocalAssignedUsers(updatedUsers);
    } catch (err) {
      logger.error("Failed to update volunteer status", { value: err });
      setStatus?.({ type: "error", message: "Unable to update volunteer status." });
    }
  };

  if (localAssignedUsers.length === 0) {
    return (
      <div className="text-muted small mt-2">
        No volunteers assigned yet.
      </div>
    );
  }

  return (
    <ul className="list-group list-group-flush mt-2">
      {localAssignedUsers.map((user) => (
        <li
          key={user.uid}
          className="list-group-item d-flex justify-content-between align-items-center px-0"
        >
          <span>
            {user.firstName} {user.lastName}
            {user.email && (
              <span className="text-muted"> ({user.email})</span>
            )}
            <span className="d-flex flex-wrap gap-3 mt-2 small">
              <label className="form-check-label"><input className="form-check-input me-1" type="checkbox" checked={user.arrived === true} onChange={(event) => updateVolunteerStatus(user.uid, "arrived", event.target.checked)} />Arrived</label>
              <label className="form-check-label"><input className="form-check-input me-1" type="checkbox" checked={user.giftReceived === true} onChange={(event) => updateVolunteerStatus(user.uid, "giftReceived", event.target.checked)} />Gift received</label>
            </span>
          </span>

          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleRemove(user.uid)}
          >
            ❌
          </button>
        </li>
      ))}
    </ul>
  );
};

export default AssignedVolunteersList;
